import { Box, Typography } from '@mui/material';
import HorizontalBarChart, { type HorizontalBarItem } from '@/components/common/charts/HorizontalBarChart';

interface RankedBarsProps<TRow> {
    rows: TRow[];
    /** Converts a row into a bar item. */
    toBar: (row: TRow) => HorizontalBarItem;
    /** Value used to rank rows from highest to lowest. */
    rank: (row: TRow) => number;
    /** Number of top rows to display. Omit to display all rows. */
    limit?: number;
    /** Colour for the value text. Defaults to each bar's own colour. */
    valueColor?: string;
}

/**
 * Renders rows as horizontal bars, optionally keeping only the highest-ranked ones.
 * Rows can be questions or projects — the caller decides via `toBar` and `rank`.
 * Used by AverageScoreBars and YesRateBars.
 */
const RankedBars = <TRow extends object>({ rows, toBar, rank, limit, valueColor }: RankedBarsProps<TRow>) => {
    const ordered = limit ? [...rows].sort((a, b) => rank(b) - rank(a)).slice(0, limit) : rows;

    return (
        <Box>
            <HorizontalBarChart rows={ordered.map(toBar)} valueColor={valueColor} />
            {limit && rows.length > limit && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Showing top {limit} of {rows.length}
                </Typography>
            )}
        </Box>
    );
};

export default RankedBars;
